import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLocale } from '../context/LocaleContext';
import { countdownRepository } from '../api/countdownRepository';
import CuteCountdownCard from '../components/CuteCountdownCard';
import CuteCountdownListRow from '../components/CuteCountdownListRow';
import CuteHeroCard from '../components/CuteHeroCard';
import CuteModal from '../components/CuteModal';
import PreviewModal from '../components/PreviewModal';
import CuteConfirmModal from '../components/CuteConfirmModal';
import ShareModal from '../components/ShareModal';
import SponsorModal from '../components/SponsorModal';
import SocialFooter from '../components/SocialFooter';
import GuestSeductionModal from '../components/GuestSeductionModal';
import SafeIcon from '../common/SafeIcon';
import { Link } from 'react-router-dom';
import { getDayDiff } from '../utils/dateUtils';

const CATEGORIES = ['all', 'life', 'work', 'birthday', 'travel', 'love', 'other'];

export default function HomePage() {
  const { user, logout } = useAuth();
  const { isDark, toggleDark } = useTheme();
  const { locale, toggleLocale, t } = useLocale();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isSponsorOpen, setIsSponsorOpen] = useState(false);
  const [isGuestSeductionOpen, setIsGuestSeductionOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  // 取代原生 alert，使用可愛的單按鈕警告視窗
  const [alertConfig, setAlertConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    theme: 'danger'
  });

  const [editingItem, setEditingItem] = useState(null);
  const [shareItem, setShareItem] = useState(null);

  const [viewMode, setViewMode] = useState(() => localStorage.getItem('dm_view_mode') || 'grid');
  const [activeTab, setActiveTab] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('default');

  // 輪播狀態
  const [activeHeroIndex, setActiveHeroIndex] = useState(0);
  const carouselRef = useRef(null);

  const userId = user?.id || user?.email || 'guest';

  // 透過每分鐘更新一次 tick，確保過了午夜 12 點時，卡片天數會自動跳轉
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setTick(prev => prev + 1);
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const data = await countdownRepository.list(userId);
      setItems(data);
    } catch (err) {
      console.error("Fetch Error:", err);
      // 隱藏後端細節
      setErrorMsg('無法連接到伺服器，請稍後再試。');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const toggleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('dm_view_mode', mode);
  };

  const handleCardClick = (item) => {
    setPreviewItem(item);
    setIsPreviewOpen(true);
  };

  const handleSave = async (data) => {
    try {
      if (editingItem) {
        await countdownRepository.update(userId, editingItem.id, data);
      } else {
        await countdownRepository.create(userId, data);
        
        // 實作：事後誘惑 (Post-Action Seduction)
        if (userId === 'guest') {
          const hasSeenSeduction = localStorage.getItem('dm_guest_seduction_seen');
          if (!hasSeenSeduction) {
            // 微延遲 400ms，讓新增視窗先關閉，體驗更滑順
            setTimeout(() => {
              setIsGuestSeductionOpen(true);
            }, 400);
            localStorage.setItem('dm_guest_seduction_seen', 'true');
          }
        }
      }
      setIsModalOpen(false);
      fetchItems();
    } catch (err) {
      console.error("Save Error Details:", err);
      
      // 統一顯示友善的錯誤，絕對不會露出伺服器技術字眼
      setAlertConfig({
        isOpen: true,
        title: t('common.saveFailed') || '儲存失敗 🥺',
        message: t('errors.createRecordFailed') || '無法寫入伺服器，請稍後再試 🥺',
        theme: 'danger'
      });
    }
  };

  const executeDelete = async () => {
    try {
      if (editingItem) {
        await countdownRepository.remove(userId, editingItem.id);
        fetchItems();
        setIsConfirmOpen(false);
        setIsModalOpen(false);
        setIsPreviewOpen(false);
      }
    } catch (err) {
      setAlertConfig({
        isOpen: true,
        title: t('common.deleteFailed') || '刪除失敗 🥺',
        message: t('errors.createRecordFailed') || '無法寫入伺服器，請稍後再試 🥺',
        theme: 'danger'
      });
    }
  };

  const executeLogout = async () => {
    await logout();
    setIsLogoutConfirmOpen(false);
  };

  const openNewModal = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const openShareModal = (item) => {
    setShareItem(item);
    setIsShareOpen(true);
  };

  const tabs = [
    { id: 'all', label: t('home.tabs.all'), icon: '🌟' },
    { id: 'future', label: t('home.tabs.future'), icon: '✨' },
    { id: 'past', label: t('home.tabs.past'), icon: '🕰️' },
  ];

  // 處理資料過濾、排序與置頂群組
  const { heroItems, futureItems, pastItems } = useMemo(() => {
    const today = new Date();
    let processedItems = items.map(item => ({
      ...item,
      _diff: getDayDiff(today, item.targetDateISO)
    }));

    if (filterCategory !== 'all') {
      processedItems = processedItems.filter(item => item.category === filterCategory);
    }

    if (activeTab === 'future') {
      processedItems = processedItems.filter(item => item._diff.status !== 'elapsed');
    } else if (activeTab === 'past') {
      processedItems = processedItems.filter(item => item._diff.status === 'elapsed');
    }

    const sortedItems = processedItems.sort((a, b) => {
      if (sortBy === 'titleAsc') return t(a.title).localeCompare(t(b.title));
      if (sortBy === 'createdDesc') return new Date(b.createdAtISO) - new Date(a.createdAtISO);
      
      const aDiff = a._diff.rawDiff;
      const bDiff = b._diff.rawDiff;
      
      if (sortBy === 'targetDesc') return bDiff - aDiff;
      
      const isFutureA = aDiff >= 0;
      const isFutureB = bDiff >= 0;
      
      if (isFutureA && !isFutureB) return -1;
      if (!isFutureA && isFutureB) return 1;
      if (isFutureA && isFutureB) return aDiff - bDiff;
      return bDiff - aDiff;
    });

    let heroes = [];
    const pinnedItems = sortedItems.filter(i => i.isPinned);
    
    if (pinnedItems.length > 0) {
      heroes = pinnedItems.sort((a, b) => {
        const diffA = a._diff.rawDiff;
        const diffB = b._diff.rawDiff;
        const isFutureA = diffA >= 0;
        const isFutureB = diffB >= 0;
        if (isFutureA && !isFutureB) return -1;
        if (!isFutureA && isFutureB) return 1;
        if (isFutureA && isFutureB) return diffA - diffB;
        return diffB - diffA;
      });
    } else if (sortedItems.length > 0) {
      heroes = [sortedItems[0]];
    }

    const finalFuture = [];
    const finalPast = [];
    sortedItems.forEach(item => {
      if (item._diff.status === 'elapsed') finalPast.push(item);
      else finalFuture.push(item);
    });

    return { heroItems: heroes, futureItems: finalFuture, pastItems: finalPast };
  }, [items, activeTab, filterCategory, sortBy, t, tick]);

  // 輪播自動播放邏輯
  useEffect(() => {
    if (!heroItems || heroItems.length <= 1) return;
    const timer = setInterval(() => {
      setActiveHeroIndex(prev => {
        const next = (prev + 1) % heroItems.length;
        const container = carouselRef.current;
        if (container && container.children[next]) {
          container.scrollTo({
            left: container.children[next].offsetLeft,
            behavior: 'smooth'
          });
        }
        return next;
      });
    }, 5000);
    return () => clearInterval(timer);
  }, [heroItems]);

  const handleCarouselScroll = (e) => {
    const container = e.target;
    const scrollLeft = container.scrollLeft;
    const width = container.clientWidth;
    if (width === 0) return;
    const index = Math.round(scrollLeft / width);
    if (index !== activeHeroIndex && index >= 0 && index < heroItems.length) {
      setActiveHeroIndex(index);
    }
  };

  const renderItemGridOrList = (itemList) => (
    <AnimatePresence mode="wait">
      {viewMode === 'grid' ? (
        <motion.div 
          key="grid-view"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        >
          {itemList.map((item) => (
            <CuteCountdownCard key={item.id} item={item} onClick={handleCardClick} onShare={openShareModal} />
          ))}
        </motion.div>
      ) : (
        <motion.div 
          key="list-view"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="flex flex-col gap-3"
        >
          {itemList.map((item) => (
            <CuteCountdownListRow key={item.id} item={item} onClick={handleCardClick} onShare={openShareModal} />
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );

  const userInitial = user?.email ? user.email.charAt(0).toUpperCase() : 'U';
  const displayEmail = user?.email || 'User';

  return (
    <div className="min-h-screen flex flex-col pb-24 sm:pb-10 transition-colors duration-300">
      <nav className="bg-white/70 dark:bg-slate-900/80 backdrop-blur-lg sticky top-0 z-40 border-b border-gray-100 dark:border-slate-800 shadow-sm transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex items-center gap-2 group hover:opacity-90 active:scale-95 transition-all focus:outline-none text-left"
              title={t('nav.brandSubtitle')}
            >
              <div className="bg-gradient-to-br from-pink-400 to-rose-400 text-white p-2 rounded-xl shadow-sm rotate-3 group-hover:rotate-6 transition-transform">
                <SafeIcon name="Heart" className="w-5 h-5" />
              </div>
              <span className="font-black text-[1.1rem] sm:text-xl text-gray-800 dark:text-white tracking-wide">
                {t('nav.brandSubtitle')} <span className="text-pink-400">DaysMatter</span>
              </span>
            </button>

            <div className="flex items-center gap-3 sm:gap-4">
              <Link to="/features" className="hidden md:flex items-center gap-1.5 text-sm font-bold text-gray-500 dark:text-gray-400 hover:text-pink-500 dark:hover:text-pink-400 transition-colors">
                <SafeIcon name="Star" className="w-4 h-4" />
                {t('nav.features')}
              </Link>
              <Link to="/help" className="hidden md:flex items-center gap-1.5 text-sm font-bold text-gray-500 dark:text-gray-400 hover:text-pink-500 dark:hover:text-pink-400 transition-colors">
                <SafeIcon name="HelpCircle" className="w-4 h-4" />
                {t('nav.help')}
              </Link>
              <button onClick={() => setIsSponsorOpen(true)} className="hidden sm:flex items-center gap-1.5 text-sm font-bold text-gray-500 dark:text-gray-400 hover:text-pink-500 dark:hover:text-pink-400 transition-colors">
                <SafeIcon name="Coffee" className="w-4 h-4" />
                {t('nav.sponsor')}
              </button>
              
              <button 
                onClick={toggleLocale}
                className="w-9 h-9 rounded-full bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700 transition-all flex items-center justify-center font-bold text-sm"
              >
                {locale === 'zh-TW' ? 'EN' : '中'}
              </button>

              <button 
                onClick={toggleDark}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-yellow-400 hover:bg-gray-200 dark:hover:bg-slate-700 transition-all"
              >
                {isDark ? <SafeIcon name="Moon" className="w-5 h-5" /> : <SafeIcon name="Sun" className="w-5 h-5" />}
              </button>
              
              <button 
                onClick={openNewModal}
                className="hidden sm:flex items-center gap-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 px-4 py-2 rounded-xl font-bold transition-all"
              >
                <SafeIcon name="Plus" className="w-4 h-4" />
                {t('nav.addEvent')}
              </button>

              {user ? (
                <div className="relative">
                  <button 
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className="bg-gradient-to-br from-purple-400 to-indigo-400 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold border-2 border-white dark:border-slate-800 shadow-sm hover:scale-105 transition-transform uppercase"
                  >
                    {userInitial}
                  </button>
                  
                  {isProfileOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setIsProfileOpen(false)}></div>
                      <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-100 dark:border-slate-700 py-2 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                        <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-700">
                          <p className="text-xs text-gray-500 dark:text-gray-400 font-bold mb-1">{t('nav.profile')}</p>
                          <p className="text-sm text-gray-800 dark:text-white font-medium truncate" title={displayEmail}>{displayEmail}</p>
                        </div>
                        <button 
                          onClick={() => {
                            setIsProfileOpen(false);
                            setIsLogoutConfirmOpen(true);
                          }}
                          className="w-full text-left px-4 py-3 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 font-bold text-sm transition-colors flex items-center gap-2"
                        >
                          <SafeIcon name="LogOut" className="w-4 h-4" />
                          {t('nav.logout')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <Link to="/login" className="text-sm font-bold text-gray-500 dark:text-gray-400 hover:text-pink-500 dark:hover:text-pink-400">
                  {t('nav.login')}
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {!user && (
        <div className="bg-amber-100/80 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 text-sm py-2 text-center font-medium backdrop-blur-sm transition-colors">
          {t('nav.guestHint')}<Link to="/login" className="underline font-bold">{t('nav.guestLink')}</Link>{t('nav.guestSave')}
        </div>
      )}

      {errorMsg && (
        <div className="bg-red-100/90 dark:bg-red-900/80 text-red-700 dark:text-red-200 text-sm py-3 px-4 text-center font-bold backdrop-blur-sm shadow-sm transition-colors break-all">
          ⚠️ {errorMsg}
        </div>
      )}

      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-10">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-gray-500">
            <SafeIcon name="Loader" className="w-10 h-10 animate-spin mb-4" />
            <p className="font-bold">{t('common.loading')}</p>
          </div>
        ) : items.length === 0 && filterCategory === 'all' ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-20 text-center"
          >
            <div className="text-8xl mb-6">🎈</div>
            <h2 className="text-2xl font-black text-gray-800 dark:text-white mb-2">{t('home.empty.title')}</h2>
            <p className="text-gray-500 dark:text-gray-400 mb-8">{t('home.empty.desc')}</p>
            <button 
              onClick={openNewModal}
              className="bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-8 py-4 rounded-full font-bold shadow-lg hover:-translate-y-1 transition-all"
            >
              {t('home.empty.button')}
            </button>
          </motion.div>
        ) : (
          <div className="space-y-8 sm:space-y-12">
            
            {heroItems.length > 0 && sortBy === 'default' && (
              <motion.section 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative -mx-4 px-4 sm:mx-0 sm:px-0 group"
              >
                <div 
                  ref={carouselRef}
                  onScroll={handleCarouselScroll}
                  className="relative flex overflow-x-auto snap-x snap-mandatory gap-4 pb-8 pt-2 w-full [&::-webkit-scrollbar]:hidden"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {heroItems.map((item) => (
                    <div key={item.id} className="w-full shrink-0 snap-center">
                      <CuteHeroCard item={item} onClick={handleCardClick} onShare={openShareModal} />
                    </div>
                  ))}
                </div>

                {heroItems.length > 1 && (
                  <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex gap-1.5 z-20 bg-black/10 dark:bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full shadow-sm">
                    {heroItems.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setActiveHeroIndex(idx);
                          const container = carouselRef.current;
                          if (container && container.children[idx]) {
                            container.scrollTo({
                              left: container.children[idx].offsetLeft,
                              behavior: 'smooth'
                            });
                          }
                        }}
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          idx === activeHeroIndex ? 'bg-white w-5' : 'bg-white/60 w-2 hover:bg-white'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </motion.section>
            )}

            <div className="sticky top-16 z-30 bg-[#fffbf0]/90 dark:bg-slate-900/90 backdrop-blur-md py-4 -mx-4 px-4 sm:mx-0 sm:px-0 transition-colors flex flex-col gap-3 sm:gap-4">
              <div className="flex gap-2 bg-white dark:bg-slate-800 p-1.5 rounded-full shadow-sm overflow-x-auto hide-scrollbar-on-mobile border border-gray-100 dark:border-slate-700 self-start w-max max-w-full">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`whitespace-nowrap px-5 py-2 rounded-full font-bold text-sm transition-all flex items-center gap-2 ${
                      activeTab === tab.id 
                        ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 shadow-sm' 
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700'
                    }`}
                  >
                    <span>{tab.icon}</span>{tab.label}
                  </button>
                ))}
              </div>

              <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between w-full">
                <div className="flex gap-2 overflow-x-auto hide-scrollbar-on-mobile w-full md:w-auto pb-1 flex-1">
                  {CATEGORIES.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setFilterCategory(cat)}
                      className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold transition-all border ${
                        filterCategory === cat 
                          ? 'bg-pink-100 dark:bg-pink-900/40 text-pink-600 dark:text-pink-300 border-pink-200 dark:border-pink-800' 
                          : 'bg-white dark:bg-slate-800 text-gray-500 dark:text-gray-400 border-gray-100 dark:border-slate-700 hover:bg-gray-50'
                      }`}
                    >
                      {t(`category.${cat}`)}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2 shrink-0 w-full md:w-auto justify-end mt-1 md:mt-0">
                  <div className="relative shrink-0">
                    <select 
                      value={sortBy} 
                      onChange={(e) => setSortBy(e.target.value)}
                      className="appearance-none bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300 text-sm font-bold py-2.5 pl-4 pr-10 rounded-full outline-none focus:border-pink-300 shadow-sm transition-colors cursor-pointer"
                    >
                      <option value="default">{t('sort.default')}</option>
                      <option value="targetDesc">{t('sort.targetDesc')}</option>
                      <option value="createdDesc">{t('sort.createdDesc')}</option>
                      <option value="titleAsc">{t('sort.titleAsc')}</option>
                    </select>
                    <SafeIcon name="ChevronDown" className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>

                  <div className="bg-white dark:bg-slate-800 p-1 rounded-full shadow-sm flex gap-1 shrink-0 border border-gray-100 dark:border-slate-700">
                    <button 
                      onClick={() => toggleViewMode('list')}
                      className={`p-1.5 rounded-full transition-colors ${viewMode === 'list' ? 'bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white' : 'text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700'}`}
                    >
                      <SafeIcon name="Menu" className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => toggleViewMode('grid')}
                      className={`p-1.5 rounded-full transition-colors ${viewMode === 'grid' ? 'bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white' : 'text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700'}`}
                    >
                      <SafeIcon name="Grid" className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {futureItems.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-6 px-2">
                  <span className="text-2xl">✨</span>
                  <h3 className="text-xl font-bold text-gray-800 dark:text-white">{t('home.sections.future')}</h3>
                  <span className="bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full text-xs font-bold ml-2">
                    {futureItems.length}
                  </span>
                </div>
                {renderItemGridOrList(futureItems)}
              </section>
            )}

            {pastItems.length > 0 && (
              <section className={futureItems.length > 0 ? "pt-4 border-t-2 border-dashed border-gray-200 dark:border-slate-700" : ""}>
                <div className="flex items-center gap-2 mb-6 px-2">
                  <span className="text-2xl opacity-80">🕰️</span>
                  <h3 className="text-xl font-bold text-slate-600 dark:text-slate-400">{t('home.sections.past')}</h3>
                  <span className="bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400 px-2 py-0.5 rounded-full text-xs font-bold ml-2">
                    {pastItems.length}
                  </span>
                </div>
                {renderItemGridOrList(pastItems)}
              </section>
            )}

            {futureItems.length === 0 && pastItems.length === 0 && heroItems.length === 0 && (
              <div className="text-center py-20 text-gray-400 font-medium bg-white/50 dark:bg-slate-800/50 rounded-3xl border border-dashed border-gray-200 dark:border-slate-700">
                {t('home.empty.noData')}
              </div>
            )}
          </div>
        )}
      </main>

      <SocialFooter onOpenSponsor={() => setIsSponsorOpen(true)} />

      <button 
        onClick={openNewModal}
        className="sm:hidden fixed bottom-6 right-6 w-16 h-16 bg-gray-900 dark:bg-white rounded-full flex items-center justify-center text-white dark:text-gray-900 shadow-xl active:scale-90 transition-transform z-40 border-4 border-[#fffbf0] dark:border-slate-900"
      >
        <SafeIcon name="Plus" className="w-8 h-8" />
      </button>

      <PreviewModal 
        isOpen={isPreviewOpen} 
        onClose={() => setIsPreviewOpen(false)} 
        item={previewItem}
        onEdit={() => {
          setEditingItem(previewItem);
          setIsPreviewOpen(false);
          setIsModalOpen(true);
        }}
        onDelete={() => {
          setEditingItem(previewItem);
          setIsPreviewOpen(false);
          setIsConfirmOpen(true);
        }}
        onShare={() => {
          setShareItem(previewItem);
          setIsShareOpen(true);
        }}
      />
      <CuteModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        onSave={handleSave}
        onDelete={editingItem ? () => {
          setIsModalOpen(false);
          setIsConfirmOpen(true);
        } : null}
        initialData={editingItem}
      />
      <CuteConfirmModal 
        isOpen={isConfirmOpen} 
        onClose={() => setIsConfirmOpen(false)} 
        onConfirm={executeDelete}
        theme="danger"
      />
      <CuteConfirmModal 
        isOpen={isLogoutConfirmOpen} 
        onClose={() => setIsLogoutConfirmOpen(false)} 
        onConfirm={executeLogout}
        title={t('confirm.logoutTitle')}
        message={t('confirm.logoutMessage')}
        confirmText={t('confirm.logoutConfirm')}
        cancelText={t('confirm.logoutCancel')}
        icon="👋"
        theme="neutral"
      />
      
      {/* 🌟 專屬錯誤彈出視窗 (自動隱藏取消按鈕版) */}
      <CuteConfirmModal 
        isOpen={alertConfig.isOpen} 
        onClose={() => setAlertConfig(prev => ({ ...prev, isOpen: false }))} 
        title={alertConfig.title}
        message={alertConfig.message}
        confirmText={t('common.gotIt')}
        hideCancel={true}
        theme={alertConfig.theme}
        icon="🚨"
      />

      <ShareModal 
        isOpen={isShareOpen} 
        onClose={() => setIsShareOpen(false)} 
        item={shareItem} 
      />
      <SponsorModal 
        isOpen={isSponsorOpen} 
        onClose={() => setIsSponsorOpen(false)} 
      />
      <GuestSeductionModal 
        isOpen={isGuestSeductionOpen} 
        onClose={() => setIsGuestSeductionOpen(false)} 
      />
    </div>
  );
}